/**
 * The Prompt Library's shapes, shared by the server that holds the prompts and
 * the page that fills them in.
 *
 * A prompt is a template plus the blanks it needs. The person fills in the
 * blanks in plain form fields, and `fillPrompt` produces the finished text they
 * copy into ChatGPT, Claude, Gemini or whatever they use. They never have to
 * find and replace a [BRACKETED PLACEHOLDER] by hand, which is the step where
 * this audience gives up — or, worse, pastes the placeholder in and gets an
 * answer about "[YOUR BUSINESS]".
 *
 * The templates themselves live on the server (server/prompts/library.ts) and
 * are only sent to Smart Stack + members. What the client may hold for anyone
 * is `PromptCard`, which deliberately has no template in it: a card that
 * carried the text would put every member-only prompt in the public bundle.
 */

export const PROMPT_CATEGORIES = [
  "Getting Started with AI",
  "Strategy & Planning",
  "Marketing & Content",
  "Brand & Voice",
  "Sales & Clients",
  "Productivity & Daily Habits",
] as const;

export type PromptCategory = (typeof PROMPT_CATEGORIES)[number];

/** Same internal names as toolsData's Role. Shown as Think / Clarify / Create. */
export type PromptRole = "Atlas" | "Comet" | "Manus";

/**
 * One option in a "pick one" blank.
 *
 * `text` is what goes into the prompt, and may itself contain {{blanks}}. That
 * is what lets one card hold seventeen prompts that each need something
 * slightly different: the form shows only the blanks the chosen option uses,
 * so picking "Newsletter from a video" and picking "Quote graphics" ask
 * different questions without any per-option configuration.
 */
export interface PromptChoice {
  /** What the dropdown shows, and what is saved as the answer. */
  label: string;
  text: string;
  /** Rendered as an <optgroup>, so a long list reads in sections. */
  group?: string;
}

export interface PromptField {
  /** Matches {{key}} in the template, or in a choice's text. */
  key: string;
  label: string;
  /** One line under the label, when the label alone is not enough. */
  help?: string;
  placeholder?: string;
  /** A textarea rather than a one-line input. */
  multiline?: boolean;
  /**
   * A "pick one" dropdown instead of a text box. Defaults to the first
   * choice, so it never needs `ifBlank`. A choice field may appear in the
   * template only, never inside another choice's text.
   */
  choices?: PromptChoice[];
  /**
   * What goes in the prompt if this is left empty.
   *
   * A string for most blanks, so a half-filled form still produces a prompt
   * that works: it tells the AI to ask for the missing piece rather than
   * leaving a hole it will quietly invent an answer for.
   *
   * `null` removes the whole line the blank sits on. That is for prompts that
   * are a list of facts about the person, like the Starter Profile, where
   * "Please don't make me sound [ask me]" is worse than saying nothing.
   * Ignored for choice fields.
   */
  ifBlank?: string | null;
}

/** What anyone may see: enough for a card and a teaser, never the prompt. */
export interface PromptCard {
  slug: string;
  title: string;
  summary: string;
  /** One italic line on the card, like a tool's "best for". */
  bestFor: string;
  category: PromptCategory;
  roles: PromptRole[];
  /** What you get back, in a few words: "A council.md file". */
  youGet: string;
}

export interface Prompt extends PromptCard {
  fields: PromptField[];
  template: string;
  /** Shown under the copy box: what to do with the prompt once it is copied. */
  howToUse?: string;
}

const BLANK = /\{\{(\w+)\}\}/g;

/** The option a choice field currently has, falling back to the first. */
export function chosenOption(
  field: PromptField,
  values: Record<string, string>
): PromptChoice | undefined {
  const choices = field.choices ?? [];
  return choices.find(c => c.label === values[field.key]) ?? choices[0];
}

/** The template with every choice blank replaced by its chosen option's text. */
function expandChoices(
  template: string,
  fields: PromptField[],
  values: Record<string, string>
): string {
  const byKey = new Map(fields.map(f => [f.key, f]));
  return template.replace(BLANK, (whole, key: string) => {
    const field = byKey.get(key);
    if (!field?.choices) return whole;
    return chosenOption(field, values)?.text ?? whole;
  });
}

/**
 * The fields the form should show right now: every choice field in the
 * template, plus the text blanks the current choices actually use. In the
 * order the fields are declared, so the dropdown can sit first.
 */
export function activeFields(
  template: string,
  fields: PromptField[],
  values: Record<string, string>
): PromptField[] {
  const expanded = expandChoices(template, fields, values);
  const used = new Set(Array.from(expanded.matchAll(BLANK), m => m[1]));
  const inTemplate = new Set(Array.from(template.matchAll(BLANK), m => m[1]));
  return fields.filter(f => (f.choices ? inTemplate.has(f.key) : used.has(f.key)));
}

/**
 * Fill a template's {{key}} blanks.
 *
 * Choices first, because a choice's text can contain blanks of its own, then
 * the text blanks, line by line so that an `ifBlank: null` blank can take its
 * line with it.
 *
 * An unknown key is left exactly as written rather than blanked, so a typo in a
 * template shows up as a visible {{typo}} in the preview instead of silently
 * dropping a sentence.
 */
export function fillPrompt(
  template: string,
  fields: PromptField[],
  values: Record<string, string>
): string {
  const byKey = new Map(fields.map(f => [f.key, f]));
  const expanded = expandChoices(template, fields, values);

  const lines: string[] = [];
  for (const line of expanded.split("\n")) {
    let drop = false;
    const out = line.replace(BLANK, (whole, key: string) => {
      const field = byKey.get(key);
      if (!field || field.choices) return whole;
      const value = (values[key] ?? "").trim();
      if (value) return value;
      if (field.ifBlank === null) {
        drop = true;
        return "";
      }
      return field.ifBlank ?? "";
    });
    if (!drop) lines.push(out);
  }
  return lines.join("\n");
}
